import React, { useCallback, useState } from 'react'
import { styled } from '@mui/material/styles'
import { Card, Skeleton as MuiSkeleton, Stack } from '@mui/material'
import { boxShadow, grayText2 } from '@/constant'
import Text from '@/components/Text'
import Badge from '@/components/Badge'
import order_bg from '@/asset/img/order_bg.webp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

export const InfoCard = styled(Card)(() => ({
  flex: 1,
  marginBottom: '24px',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  boxShadow,
}))

export const CardWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: 'true' | 'false' }>(({ disabled }) => ({
  background: '#fff',
  position: 'relative',
  flex: '0 0 365px',
  zIndex: 0,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    backgroundImage: `url(${order_bg})`,
    width: '100%',
    height: '100%',
    transform: 'scale(3)',
    backgroundPosition: 'center',
    opacity: 0,
    zIndex: '-1',
    transformOrigin: 'center',
    transition: 'transform 0.2s ease-in-out,opacity 0.2s ease-in-out',
  },
  ...(disabled !== 'true' && {
    '&:hover': {
      boxShadow,
      color: '#fff',
      transition: 'box-shadow 0.2s ease-in-out',
      '&::before': {
        opacity: 1,
        transform: 'scale(1.5)',
        transformOrigin: 'center',
        transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out ',
      },
    },
  }),
}))

export const OrderCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: 'true' | 'false' }>(({ disabled }) => ({
  zIndex: 2,
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  background: 'transparent',
  height: '100%',
  overflow: 'hidden',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  ...(disabled === 'true'
    ? {
        color: '#BFBFBF',
        '#order-name': {
          color: '#BFBFBF',
        },
        '#order-price #order-price-symbol': {
          color: '#BFBFBF',
        },
        '#order-price #order-price-number': {
          color: '#BFBFBF',
        },
        '#order-detail #order-detail-label': {
          color: '#BFBFBF',
        },
        '#order-detail #order-detail-content': {
          color: '#BFBFBF',
        },
      }
    : {
        '&:hover': {
          color: '#fff',
          '#order-name': {
            color: '#fff',
            transition: 'all 0.2s ease-in-out',
          },
          '#order-created-time': {
            color: '#fff',
            transition: 'all 0.2s ease-in-out',
          },
          '#order-created-time #order-status': {
            color: '#fff',
            borderColor: '#fff',
            background: 'transparent',
            transition: 'all 0.2s ease-in-out',
          },
          '#order-price #order-price-symbol': {
            color: '#fff',
            transition: 'all 0.2s ease-in-out',
          },
          '#order-price #order-price-number': {
            color: '#fff',
            transition: 'all 0.2s ease-in-out',
          },
          '#order-detail #order-detail-label': {
            color: '#fff',
            transition: 'all 0.2s ease-in-out',
          },
          '#order-detail #order-detail-content': {
            color: '#fff',
            transition: 'all 0.2s ease-in-out',
          },
          '#order-card-divider': {
            backgroundImage:
              'linear-gradient(243deg, rgba(217, 217, 217, 0), rgba(217, 217, 217, 0.6), rgba(255, 255, 255, 1), rgba(217, 217, 217, 0.6), rgba(217, 217, 217, 0)) 1 1;',
          },
        },
      }),
}))

export const OrderListWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  gap: '16px',
}))

export const Row = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
}))

export const Divider = styled('div')(() => ({
  height: '2px',
  width: '100%',
  border: '1px solid',
  borderImage:
    'linear-gradient(243deg, rgba(217, 217, 217, 0), rgba(217, 217, 217, 0.6), rgba(217, 217, 217, 1), rgba(217, 217, 217, 0.6), rgba(217, 217, 217, 0)) 1 1;',
}))

export const Skeleton = styled(MuiSkeleton)(() => ({
  variant: 'rectangular',
  borderRadius: '0',
}))

export const PayTypeCard = styled('div')(() => ({
  display: 'flex',
  padding: '24px',
  backgroundColor: '#f7f7f780',
  margin: '16px 0',
  flexDirection: 'column',
  gap: '16px',
}))

export const ExpandableRow = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: 'true' | 'false' }>(({ expanded }) => ({
  alignItems: 'flex-start',
  height: '24px',
  transition: 'all 0.2s ease-in-out',
  overflow: 'hidden',
  backgroundColor: 'rgba(247,247,247,0.3)',
  padding: '0px 4px',
  '& > .arrow': {
    cursor: 'pointer',
    position: 'sticky',
    top: '0',
    transition: 'transform 0.2s ease-in-out',
  },
  ...(expanded === 'true' && {
    height: '200px',
    overflow: 'auto',
    ...(navigator?.userAgent?.indexOf('Chrome') != -1 && {
      overflow: 'overlay',
    }),
    transition: 'all 0.2s ease-in-out',
    '& > .arrow': {
      position: 'sticky',
      top: '0',
      transform: 'rotate(180deg)',
      transition: 'transform 0.2s ease-in-out',
    },
  }),
}))
export const DetailItem = ({
  label,
  content,
  listView,
}: {
  label: string;
  content:
    | string
    | number
    | React.ReactNode
    | { name: string; value: string }[];
  listView?: boolean;
}) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const onClickExpandRow = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      setExpanded(!expanded)
    },
    [expanded]
  )

  const renderContent = (
    content:
      | string
      | number
      | React.ReactNode
      | { name: string; value: string }[]
  ) => {
    if (typeof content === 'string' || typeof content === 'number') {
      return (
        <Text
          id="order-detail-content"
          size="xs"
          weight="bold"
          sx={{
            overflowWrap: 'anywhere',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {content}
        </Text>
      )
    } else if (listView && Array.isArray(content)) {
      return (
        <ExpandableRow
          expanded={expanded ? 'true' : 'false'}
          onClick={onClickExpandRow}
        >
          <Stack>
            {content?.map((item, index) => (
              <Text
                key={`order-detail-content-${index}`}
                id="order-detail-content"
                size="xs"
                weight="bold"
              >
                {item?.name} - {item?.value}
              </Text>
            ))}
          </Stack>
          <ArrowDropDownIcon className="arrow" />
        </ExpandableRow>
      )
    } else if (content) {
      return React.cloneElement(content as React.ReactElement, {
        id: 'order-detail-content',
      })
    } else {
      return null
    }
  }

  return (
    <Row id="order-detail" sx={{ gap: '16px', alignItems: 'flex-start' }}>
      <Text
        id="order-detail-label"
        size="xs"
        sx={{ color: grayText2, minWidth: '64px' }}
        weight="regular"
      >
        {label}
      </Text>
      {renderContent(content)}
    </Row>
  )
}

export const OrderStatus = styled(Badge)(() => ({
  transform: 'skew(-15deg)',
  borderRadius: '4px',
  fontSize: '12px',
  '& #status-content': {
    transform: 'skew(15deg)',
  },
  '& > svg': {
    transform: 'skew(15deg) scale(0.8)',
  },
}))
